import { Injectable, inject } from '@angular/core';
import { ofType, createEffect } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { of, catchError, map, switchMap, exhaustMap } from 'rxjs';

import { BaseEffects } from '../../../base.effects';
import { CONFIRM_SMS_DIALOG } from '../../../tokens/auth-dialog.tokens';
import { NotificationActions } from '../../notifications/notification.actions';
import { AuthStateSelectors } from '../auth.selectors';
import { ConfirmSmsDialogOutput } from '../dialogs/auth-dialog.contracts';
import { LogOutActions } from '../logout/logout.actions';
import { AmplifyService } from '../services/amplify.service';

import { LoginActions } from './login.actions';

const mfaIncorrectCodeMessage = 'The code you entered was incorrect. Please try again.';
const mfaResendSmsCodeMessage = 'New verification code sent via SMS';

@Injectable()
export class LoginMfaEffects extends BaseEffects {
  private readonly amplifyService = inject(AmplifyService);
  private readonly openConfirmSms = inject(CONFIRM_SMS_DIALOG);

  displayMfaChallenge$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.displayMFAChallenge),
      exhaustMap(({ signInOutput }) => {
        const destination = signInOutput ? (signInOutput.nextStep as any)?.codeDeliveryDetails?.destination : '';
        return this.openConfirmSms({
          data: { destination, resendAction: LoginActions.resendSmsCode() },
        })
          .afterClosed()
          .pipe(
            map(({ code, cancelled }: ConfirmSmsDialogOutput) =>
              cancelled ? LoginActions.cancelSmsVerification() : LoginActions.submitSmsCode({ code, signInOutput })
            )
          );
      })
    )
  );

  submitSmsCode$ = this.switchMapEffect({
    action: LoginActions.submitSmsCode,
    switchMapFn: ({ code, signInOutput }) =>
      this.amplifyService.confirmSignIn(code).pipe(
        map(signInOutput => LoginActions.submitSmsCodeSuccess({ signInOutput })),
        catchError((error: unknown) => of(LoginActions.submitSmsCodeFailed({ error, signInOutput })))
      ),
  });

  submitSmsCodeFail_displayChallengeAgain$ = this.mapEffect({
    action: LoginActions.submitSmsCodeFailed,
    mapFn: ({ signInOutput }) => LoginActions.displayMFAChallenge({ signInOutput }),
  });

  submitSmsCodeFail_notify$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.submitSmsCodeFailed),
      map(() => NotificationActions.showSnackbar({ message: mfaIncorrectCodeMessage }))
    )
  );

  submitSmsCodeSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.submitSmsCodeSuccess),
      map(({ signInOutput }) => LoginActions.logInNextStep({ signInOutput }))
    )
  );

  cancelSmsVerification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.cancelSmsVerification),
      switchMap(() => this.amplifyService.signOut().pipe(map(() => LogOutActions.amplifyLoggedOut())))
    )
  );

  resendSmsCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoginActions.resendSmsCode),
      concatLatestFrom(() => this.store.select(AuthStateSelectors.loginEmailAddress)),
      switchMap(([_, email]) =>
        this.amplifyService
          .resendSignupCode(email)
          .pipe(map(() => NotificationActions.showSnackbar({ message: mfaResendSmsCodeMessage })))
      )
    )
  );
}
